import { useCallback, useEffect, useState } from "react";
import { findAll } from "@/apis/admin-board";
import { AdminBoardItemDto } from "@/dto/AdminBoardDto";
import { enqueueSnackbar } from "notistack";

interface UseAdminBoardListProps {
  comId: string;
  initialPage?: number;
  initialLimit?: number;
}

export default function useAdminBoardList({
  comId,
  initialPage = 1,
  initialLimit = 50,
}: UseAdminBoardListProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [boardList, setBoardList] = useState<AdminBoardItemDto[]>([]);
  const [page, setPage] = useState<number>(initialPage);
  const [limit, setLimit] = useState<number>(initialLimit);
  const [totalCount, setTotalCount] = useState<number>(0);

  /**
   * 게시판 목록 조회
   */
  const fetchBoardList = useCallback(async () => {
    // comId가 없거나 빈 문자열인 경우 API 호출하지 않음
    if (!comId) {
      setBoardList([]);
      setTotalCount(0);
      console.log("회사 ID가 없어 게시판 목록을 조회하지 않습니다.");
      return;
    }

    console.log("게시판 목록 조회 시작:", new Date().toISOString());
    try {
      setIsLoading(true);
      const response = await findAll(comId, page, limit);
      console.log("게시판 API 응답 데이터:", response);

      if (response && response.success) {
        // 응답 데이터 처리
        const data = response.data;
        setBoardList(data.list || []);
        setTotalCount(data.count || 0);
        console.log("새로운 게시판 목록:", data.list);
      } else {
        enqueueSnackbar("게시판 목록을 불러오는데 실패했습니다.", {
          variant: "error",
        });
        setBoardList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("게시판 목록 조회 중 오류 발생:", error);
      enqueueSnackbar("게시판 목록을 불러오는데 실패했습니다.", {
        variant: "error",
      });
      setBoardList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      console.log("게시판 목록 조회 완료:", new Date().toISOString());
    }
  }, [comId, page, limit]);

  /**
   * 페이지 변경 핸들러
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  /**
   * 페이지당 항목 수 변경 핸들러
   */
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // 페이지당 항목 수가 변경되면 첫 페이지로 이동
  };

  /**
   * 강제 새로고침 함수
   */
  const forceRefresh = useCallback(async () => {
    // 상태를 초기화하여 컴포넌트가 리렌더링되도록 합니다
    setBoardList([]);
    setTotalCount(0);

    // 약간의 딜레이 후 데이터를 다시 불러옵니다
    setTimeout(async () => {
      await fetchBoardList();
    }, 100);
  }, [fetchBoardList]);

  // comId가 변경되면 페이지를 1로 초기화
  useEffect(() => {
    setPage(1);
  }, [comId]);

  // 컴포넌트 마운트 시와 의존성 변경 시 게시판 목록 조회
  useEffect(() => {
    fetchBoardList();
  }, [fetchBoardList]);

  return {
    isLoading,
    boardList,
    page,
    limit,
    totalCount,
    handlePageChange,
    handleLimitChange,
    fetchBoardList,
    forceRefresh,
  };
}
